import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export const purchaseOrderApi = createApi({
  reducerPath: 'purchaseOrder',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PurchaseOrder', 'PurchaseOrderFormFieldConfig', 'PurchaseOrderApprovalConfig', 'PurchaseOrderTableConfig'],
  endpoints: (builder) => ({
    getPurchaseOrders: builder.query<BackendResponse<{ data: any[]; total: number; page: number; pageSize: number }>, any>({
      query: (query) => ({
        url: `/purchase-order${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrder'],
    }),

    getPurchaseOrderById: builder.query<BackendResponse<any>, number>({
      query: (id) => ({
        url: `/purchase-order/${id}`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrder'],
    }),

    getPOVendors: builder.query<BackendResponse<any[]>, void>({
      query: () => ({
        url: '/purchase-order/masters/vendors',
        method: 'GET',
      }),
    }),

    derivePODefaults: builder.mutation<BackendResponse<{
      values: Record<string, unknown>;
      provenance: Array<{ field: string; source: string; sourceId: number; overridable: boolean }>;
      warnings: string[];
    }>, { companyId?: number; vendorId?: number; materialId?: number; target: 'HEADER' | 'LINE' }>({
      query: body => ({
        url: '/purchase-order/derive-defaults',
        method: 'POST',
        body,
      }),
    }),

    simulatePurchaseOrder: builder.mutation<BackendResponse<any>, Record<string, unknown>>({
      query: body => ({
        url: '/purchase-order/simulate',
        method: 'POST',
        body,
      }),
    }),

    getPOMasterDefaults: builder.query<BackendResponse<any>, { companyId?: number }>({
      query: query => ({
        url: `/purchase-order-config/master-defaults${parseQueryParams(query)}`,
        method: 'GET',
      }),
    }),

    savePOMasterDefaults: builder.mutation<BackendResponse<any>, {
      companyId?: number;
      configValue: Record<string, unknown>;
    }>({
      query: body => ({
        url: '/purchase-order-config/master-defaults',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),

    exportPOMasterDefaults: builder.mutation<Blob, { companyId?: number }>({
      query: query => ({
        url: `/purchase-order-config/master-defaults/export${parseQueryParams(query)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    importPOMasterDefaults: builder.mutation<BackendResponse<any>, {
      file: File; companyId?: number; dryRun: boolean;
    }>({
      query: ({ file, companyId, dryRun }) => {
        const body = new FormData();
        body.append('file', file);
        return {
          url: `/purchase-order-config/master-defaults/import${parseQueryParams({ companyId, dryRun })}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),

    getPOMasterDefaultAudit: builder.query<BackendResponse<any>, number>({
      query: id => ({ url: `/purchase-order-config/master-defaults/${id}/audit` }),
    }),

    getPOFinancialPolicies: builder.query<BackendResponse<any>, Record<string, unknown>>({
      query: query => ({
        url: `/purchase-order-config/financial-policies${parseQueryParams(query)}`,
        method: 'GET',
      }),
    }),

    savePOFinancialPolicy: builder.mutation<BackendResponse<any>, { id?: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/purchase-order-config/financial-policies${id ? `/${id}` : ''}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),

    deletePOFinancialPolicies: builder.mutation<BackendResponse<any>, { ids: number[] }>({
      query: body => ({
        url: '/purchase-order-config/financial-policies',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),

    exportPOFinancialPolicies: builder.mutation<Blob, Record<string, unknown>>({
      query: query => ({
        url: `/purchase-order-config/financial-policies/export${parseQueryParams(query)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    importPOFinancialPolicies: builder.mutation<BackendResponse<any>, { file: File; dryRun: boolean }>({
      query: ({ file, dryRun }) => {
        const body = new FormData();
        body.append('file', file);
        return {
          url: `/purchase-order-config/financial-policies/import${parseQueryParams({ dryRun })}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),

    getPOFinancialPolicyAudit: builder.query<BackendResponse<any>, number>({
      query: id => ({ url: `/purchase-order-config/financial-policies/${id}/audit` }),
    }),

    createPurchaseOrderDraft: builder.mutation<BackendResponse<any>, { companyId?: number }>({
      query: (payload) => ({
        url: `/purchase-order/create-draft`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    updatePurchaseOrderDraft: builder.mutation<BackendResponse<any>, { id: number; [key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/purchase-order/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    submitPurchaseOrder: builder.mutation<BackendResponse<any>, number>({
      query: (id) => ({
        url: `/purchase-order/${id}/submit`,
        method: 'POST',
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    deletePurchaseOrders: builder.mutation<BackendResponse<any>, { ids: number[] }>({
      query: (payload) => ({
        url: `/purchase-order`,
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    submitPOApprovalAction: builder.mutation<BackendResponse<any>, { id: number; action: string; comments?: string }>({
      query: ({ id, ...body }) => ({
        url: `/purchase-order/${id}/approval-action`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    uploadPOAttachment: builder.mutation<BackendResponse<any>, { id: number; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/purchase-order/${id}/attachments`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    getPOAttachments: builder.query<BackendResponse<any[]>, number>({
      query: (id) => ({
        url: `/purchase-order/${id}/attachments`,
        method: 'GET',
      }),
    }),

    deletePOAttachment: builder.mutation<BackendResponse<any>, { id: number; attachmentId: number }>({
      query: ({ id, attachmentId }) => ({
        url: `/purchase-order/${id}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    bulkUploadPOLineItems: builder.mutation<BackendResponse<any[]>, { id: number; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/purchase-order/${id}/line-items/bulk-upload`,
        method: 'POST',
        body: formData,
      }),
    }),

    bulkImportPOLineItems: builder.mutation<BackendResponse<any>, { id: number; items: any[] }>({
      query: ({ id, items }) => ({
        url: `/purchase-order/${id}/line-items/bulk-import`,
        method: 'POST',
        body: { items },
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    downloadPOJson: builder.mutation<BackendResponse<any[]>, number>({
      query: (id) => ({
        url: `/purchase-order/download/json`,
        method: 'POST',
        body: { id },
      }),
    }),

    pushPOToErp: builder.mutation<BackendResponse<any>, number>({
      query: (id) => ({
        url: `/purchase-order/push/erp`,
        method: 'POST',
        body: { id },
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    getPORecentDrafts: builder.query<BackendResponse<any[]>, void>({
      query: () => ({
        url: `/purchase-order/recent-drafts`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrder'],
    }),

    getPOFormFieldConfigByCompany: builder.query<BackendResponse<any>, {
      companyId: number;
      poTypeId?: number;
      purchaseGroupId?: number;
      procurementCategoryId?: number;
    }>({
      query: ({ companyId, ...dimensions }) => ({
        url: `/purchase-order/form-field-mappings/by-company/${companyId}${parseQueryParams(dimensions)}`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrderFormFieldConfig'],
    }),

    getPOStats: builder.query<BackendResponse<{
      total: number;
      drafts: number;
      pending: number;
      approved: number;
      rejected: number;
      onHold: number;
      myPendingApprovals: number;
      overdueApprovals: number;
      dueSoonApprovals: number;
      activeVendors: number;
      submittedLast30Days: number;
      approvedLast30Days: number;
      totalCommittedValue: number;
      approvedValue: number;
      pendingValue: number;
      sap: { pending: number; failed: number };
    }>, void>({
      query: () => ({
        url: `/purchase-order/stats`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrder'],
    }),

    getPOTableConfigs: builder.query<BackendResponse<any[]>, string>({
      query: page => ({ url: `/purchase-order-table-config${parseQueryParams({ page })}`, method: 'GET' }),
      providesTags: ['PurchaseOrderTableConfig'],
    }),

    getPOActiveTableConfig: builder.query<BackendResponse<any>, string>({
      query: (page) => ({
        url: `/purchase-order-table-config/active${parseQueryParams({ page })}`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrderTableConfig'],
    }),

    createPOTableConfig: builder.mutation<BackendResponse<any>, any>({
      query: (body) => ({
        url: '/purchase-order-table-config',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PurchaseOrderTableConfig'],
    }),

    applyPOTableConfig: builder.mutation<BackendResponse<any>, { configId: number; page: string }>({
      query: (body) => ({
        url: '/purchase-order-table-config/apply',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PurchaseOrderTableConfig'],
    }),

    resetPOApprovalHierarchy: builder.mutation<BackendResponse<any>, number>({
      query: (id) => ({
        url: `/purchase-order/${id}/reset-hierarchy`,
        method: 'POST',
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    getPOLogs: builder.query<BackendResponse<{ activities: any[]; changeLogs: any[]; approvalLogs: any[] }>, number>({
      query: (id) => ({
        url: `/purchase-order/${id}/logs`,
        method: 'GET',
      }),
      providesTags: ['PurchaseOrder'],
    }),

    getPOFormFieldMappings: builder.query<BackendResponse<any>, any>({
      query: query => ({ url: `/purchase-order-config/form-field-mappings${parseQueryParams(query)}`, method: 'GET' }),
      providesTags: ['PurchaseOrderFormFieldConfig'],
    }),
    getPOFormFieldMapping: builder.query<BackendResponse<any>, number>({
      query: id => ({ url: `/purchase-order-config/form-field-mappings/${id}`, method: 'GET' }),
      providesTags: ['PurchaseOrderFormFieldConfig'],
    }),
    getPOUniversalFields: builder.query<BackendResponse<any[]>, void>({
      query: () => ({ url: '/purchase-order-config/form-field-mappings/universal-fields', method: 'GET' }),
    }),
    getPOConfigurationAudit: builder.query<BackendResponse<any[]>, { mode: 'form-fields' | 'approvals'; id: number }>({
      query: ({ mode, id }) => ({ url: `/purchase-order-config/${mode === 'approvals' ? 'approval-mappings' : 'form-field-mappings'}/${id}/audit`, method: 'GET' }),
    }),
    savePOFormFieldMapping: builder.mutation<BackendResponse<any>, { id?: number; body: any }>({
      query: ({ id, body }) => ({
        url: `/purchase-order-config/form-field-mappings${id ? `/${id}` : ''}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),
    deletePOFormFieldMappings: builder.mutation<BackendResponse<any>, { ids: number[] }>({
      query: body => ({ url: '/purchase-order-config/form-field-mappings', method: 'DELETE', body }),
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),
    importPOFormFieldMappings: builder.mutation<BackendResponse<any>, { body: FormData; dryRun?: boolean }>({
      query: ({ body, dryRun = true }) => ({ url: `/purchase-order-config/form-field-mappings/import?dryRun=${dryRun}`, method: 'POST', body }),
      invalidatesTags: ['PurchaseOrderFormFieldConfig'],
    }),
    getPOApprovalMappings: builder.query<BackendResponse<any>, any>({
      query: query => ({ url: `/purchase-order-config/approval-mappings${parseQueryParams(query)}`, method: 'GET' }),
      providesTags: ['PurchaseOrderApprovalConfig'],
    }),
    getPOApprovalMapping: builder.query<BackendResponse<any>, number>({
      query: id => ({ url: `/purchase-order-config/approval-mappings/${id}`, method: 'GET' }),
      providesTags: ['PurchaseOrderApprovalConfig'],
    }),
    explainPOApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: query => ({ url: `/purchase-order-config/approval-mappings/explain${parseQueryParams(query)}`, method: 'GET' }),
    }),
    savePOApprovalMapping: builder.mutation<BackendResponse<any>, { id?: number; body: any }>({
      query: ({ id, body }) => ({
        url: `/purchase-order-config/approval-mappings${id ? `/${id}` : ''}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: ['PurchaseOrderApprovalConfig'],
    }),
    deletePOApprovalMappings: builder.mutation<BackendResponse<any>, { ids: number[] }>({
      query: body => ({ url: '/purchase-order-config/approval-mappings', method: 'DELETE', body }),
      invalidatesTags: ['PurchaseOrderApprovalConfig'],
    }),
    importPOApprovalMappings: builder.mutation<BackendResponse<any>, { body: FormData; dryRun?: boolean }>({
      query: ({ body, dryRun = true }) => ({ url: `/purchase-order-config/approval-mappings/import?dryRun=${dryRun}`, method: 'POST', body }),
      invalidatesTags: ['PurchaseOrderApprovalConfig'],
    }),
  }),
});

export const {
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderByIdQuery,
  useGetPOVendorsQuery,
  useDerivePODefaultsMutation,
  useSimulatePurchaseOrderMutation,
  useGetPOMasterDefaultsQuery,
  useSavePOMasterDefaultsMutation,
  useExportPOMasterDefaultsMutation,
  useImportPOMasterDefaultsMutation,
  useLazyGetPOMasterDefaultAuditQuery,
  useGetPOFinancialPoliciesQuery,
  useSavePOFinancialPolicyMutation,
  useDeletePOFinancialPoliciesMutation,
  useExportPOFinancialPoliciesMutation,
  useImportPOFinancialPoliciesMutation,
  useLazyGetPOFinancialPolicyAuditQuery,
  useCreatePurchaseOrderDraftMutation,
  useUpdatePurchaseOrderDraftMutation,
  useSubmitPurchaseOrderMutation,
  useDeletePurchaseOrdersMutation,
  useSubmitPOApprovalActionMutation,
  useUploadPOAttachmentMutation,
  useGetPOAttachmentsQuery,
  useDeletePOAttachmentMutation,
  useBulkUploadPOLineItemsMutation,
  useBulkImportPOLineItemsMutation,
  useDownloadPOJsonMutation,
  usePushPOToErpMutation,
  useGetPORecentDraftsQuery,
  useGetPOFormFieldConfigByCompanyQuery,
  useGetPOStatsQuery,
  useGetPOTableConfigsQuery,
  useGetPOActiveTableConfigQuery,
  useCreatePOTableConfigMutation,
  useApplyPOTableConfigMutation,
  useResetPOApprovalHierarchyMutation,
  useGetPOLogsQuery,
  useGetPOFormFieldMappingsQuery,
  useGetPOFormFieldMappingQuery,
  useGetPOUniversalFieldsQuery,
  useGetPOConfigurationAuditQuery,
  useSavePOFormFieldMappingMutation,
  useDeletePOFormFieldMappingsMutation,
  useImportPOFormFieldMappingsMutation,
  useGetPOApprovalMappingsQuery,
  useGetPOApprovalMappingQuery,
  useExplainPOApprovalMappingMutation,
  useSavePOApprovalMappingMutation,
  useDeletePOApprovalMappingsMutation,
  useImportPOApprovalMappingsMutation,
} = purchaseOrderApi;

export default purchaseOrderApi;
